import COLORS from './colors.json';
import { varAlpha, createPaletteChannel } from '../styles';

// ----------------------------------------------------------------------

/**
 * The ramps in `colors.json` come from the Dabang template, with one correction.
 *
 * Dabang's own ramps aren't ordered by lightness — its error ramp is main #FA5A7D but
 * dark #FF947A, and warning's darker (#FFA800) is lighter than its main. That breaks every
 * soft `Label`/`Chip`/`Button` in this app, because the soft variant paints
 * `[color].dark` on 16%-alpha `[color].main` (see components/label/styles.js): a "dark" step
 * lighter than the fill lands around 1.8:1.
 *
 * So each ramp keeps Dabang's `main` (and its `lighter` tint where it had a usable one) and
 * derives `light`/`dark`/`darker` as ordered steps, picked so the soft variant clears 4.5:1
 * on its own tinted background. Known exception: filled error labels put white on #FA5A7D at
 * 3.08:1 — inherited from the template's pink, same as MUI's own #f44336.
 *
 * `contrastText` is dark rather than white on success and warning: Dabang's mint (1.7:1) and
 * amber (2.0:1) are fill colours, not backgrounds for white text.
 */

// Grey
export const grey = createPaletteChannel(COLORS.grey);

// Primary
export const primary = createPaletteChannel(COLORS.primary);

// Secondary
export const secondary = createPaletteChannel(COLORS.secondary);

// Info
export const info = createPaletteChannel(COLORS.info);

// Success
export const success = createPaletteChannel(COLORS.success);

// Warning
export const warning = createPaletteChannel(COLORS.warning);

// Error
export const error = createPaletteChannel(COLORS.error);

// Common
export const common = createPaletteChannel(COLORS.common);

// Text
// Deliberately still the grey ramp: Dabang sets text.secondary to a blue (#4079ED) which
// measures 4.10:1 on white, and this app's secondary line inside grid cells is 11px.
export const text = {
  light: createPaletteChannel({
    primary: grey[800],
    secondary: grey[600],
    disabled: grey[500],
  }),
  dark: createPaletteChannel({
    primary: '#FFFFFF',
    secondary: grey[500],
    disabled: grey[600],
  }),
};

// Background
export const background = {
  light: createPaletteChannel({
    paper: '#FFFFFF',
    // Dabang's canvas — its `neutral.lighter`, the colour its `<main>` is painted with. Cooler
    // and lighter than the Material template's #F5F5F5, and it doubles as the grid row hover.
    default: '#F7FAFC',
    neutral: grey[200],
  }),
  dark: createPaletteChannel({
    paper: grey[800],
    default: grey[900],
    neutral: '#28323D',
  }),
};

// Action
export const baseAction = {
  hover: varAlpha(grey['500Channel'], 0.08),
  selected: varAlpha(grey['500Channel'], 0.16),
  focus: varAlpha(grey['500Channel'], 0.24),
  disabled: varAlpha(grey['500Channel'], 0.8),
  disabledBackground: varAlpha(grey['500Channel'], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

export const action = {
  light: { ...baseAction, active: grey[600] },
  dark: { ...baseAction, active: grey[500] },
};

/*
 * Base palette
 */
export const basePalette = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: varAlpha(grey['500Channel'], 0.2),
  action,
};

export const lightPalette = {
  ...basePalette,
  text: text.light,
  background: background.light,
  action: action.light,
};

export const darkPalette = {
  ...basePalette,
  text: text.dark,
  background: background.dark,
  action: action.dark,
};

// ----------------------------------------------------------------------

export const colorSchemes = {
  light: { palette: lightPalette },
  dark: { palette: darkPalette },
};
